'use client';

import React from 'react';

export interface CaptionProps {
    type: 'overlay' | 'interstitial' | 'hero';
    text: string;
    subtext?: string;
    cta?: {
        text: string;
        href: string;
    };
}

export function Caption({ type, text, subtext, cta }: CaptionProps) {
    if (type === 'hero') {
        return (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/98 backdrop-blur-xl animate-in fade-in duration-1000 p-8">
                <div className="text-center w-full max-w-4xl flex flex-col items-center">
                    <h3 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight animate-in slide-in-from-bottom-12 duration-1000 fill-mode-forwards bg-clip-text text-transparent bg-gradient-to-br from-gray-900 to-indigo-800">
                        {text}
                    </h3>
                    {subtext && (
                        <p className="text-lg md:text-xl text-violet-600 font-medium tracking-wide animate-in fade-in duration-1000 delay-500 fill-mode-forwards mb-8">
                            {subtext}
                        </p>
                    )}
                    {cta && (
                        <a
                            href={cta.href}
                            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-violet-600 text-white font-semibold text-base sm:text-lg rounded-2xl hover:bg-violet-700 transition-all shadow-xl shadow-violet-600/20 hover:shadow-2xl hover:shadow-violet-600/30 hover:-translate-y-0.5 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-700 fill-mode-forwards"
                        >
                            {cta.text}
                        </a>
                    )}
                </div>
            </div>
        );
    }
    if (type === 'interstitial') {
        return (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm animate-in fade-in duration-500">
                <div className="text-center px-4 max-w-lg">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight animate-in slide-in-from-bottom-4 duration-700">{text}</h3>
                    {subtext && <p className="text-lg text-gray-500 animate-in slide-in-from-bottom-3 duration-700 delay-100">{subtext}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="absolute bottom-4 md:bottom-8 left-0 right-0 z-50 flex justify-center pointer-events-none px-4">
            <div className="bg-[#2e2e48]/95 backdrop-blur-md text-white px-4 py-3 md:px-6 md:py-3 rounded-2xl shadow-2xl flex flex-col md:flex-row items-center gap-2 md:gap-4 animate-in slide-in-from-bottom-4 fade-in duration-500 max-w-[95%] md:max-w-lg ring-1 ring-white/10">
                <div className="hidden md:flex w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse shadow-[0_0_10px_rgba(74,222,128,0.5)]"></div>
                <div className="text-center md:text-left flex flex-col md:block">
                    <span className="font-bold text-sm md:text-base md:mr-3 tracking-wide">{text}</span>
                    {subtext && <span className="text-gray-300 text-xs md:text-sm md:border-l md:border-gray-600 md:pl-3 whitespace-normal md:whitespace-nowrap mt-0.5 md:mt-0">{subtext}</span>}
                </div>
            </div>
        </div>
    );
}
